import React, { useState, useEffect } from 'react';
import { Plus, Wrench, Truck, FileClock, X, CheckCircle2, ClipboardList, Boxes, Pencil, Trash2 } from 'lucide-react';
import PageLayout from '../../components/PageLayout';
import Modal from '../../components/Modal';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';
import DataGrid from '../../components/DataGrid';
import SearchSelect from '../../components/SearchSelect';
import {
  vendors as vendorApi,
  assets as assetApi,
  amcContracts as amcApi,
  purchaseOrders as poApi,
  inventory as inventoryApi,
  uploads as uploadsApi,
  type Vendor,
  type Asset,
  type PmSchedule,
  type AssetMaintenanceLog,
  type AmcContract,
  type PurchaseOrder,
  type InventoryItem,
  type InventoryStockTransaction,
  type ApiError,
} from '../../services/api';

const inp = {
  width: '100%', padding: '9px 12px', background: 'var(--nx-surface-3)',
  border: '1px solid var(--nx-border)', borderRadius: 8, fontSize: 13,
  color: 'var(--nx-text-primary)', outline: 'none', boxSizing: 'border-box' as const,
};
const lbl = {
  display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-secondary)',
  marginBottom: 5, letterSpacing: '0.05em', textTransform: 'uppercase' as const,
};
const actionBtn: React.CSSProperties = {
  background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px',
  borderRadius: 6, color: 'var(--nx-text-muted)', display: 'inline-flex', alignItems: 'center',
};

type Tab = 'assets' | 'vendors' | 'amc' | 'po' | 'inventory';

const fmtAmount = (n: number | undefined) =>
  n == null ? '—' : `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const AssetMaintenancePage: React.FC = () => {
  const { user } = useAuth();
  const societyId = user?.societyId ?? null;
  const [tab, setTab] = useState<Tab>('assets');

  return (
    <PageLayout>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, fontWeight: 700, color: 'var(--nx-text-primary)', letterSpacing: '-0.03em', marginBottom: 4 }}>
          Assets & Vendors
        </h1>
        <p style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>Vendors, asset registry, preventive maintenance and AMC contracts.</p>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {(['assets', 'vendors', 'amc', 'po', 'inventory'] as Tab[]).map(t => (
          <button
            key={t} onClick={() => setTab(t)}
            className={tab === t ? 'nx-btn nx-btn-primary nx-btn-sm' : 'nx-btn nx-btn-outline nx-btn-sm'}
          >
            {t === 'assets' ? 'Assets' : t === 'vendors' ? 'Vendors' : t === 'amc' ? 'AMC Contracts' : t === 'po' ? 'Purchase Orders' : 'Inventory'}
          </button>
        ))}
      </div>

      {!societyId ? null : tab === 'vendors' ? <VendorsTab societyId={societyId} />
        : tab === 'assets' ? <AssetsTab societyId={societyId} />
        : tab === 'amc' ? <AmcTab societyId={societyId} />
        : tab === 'po' ? <PurchaseOrdersTab societyId={societyId} />
        : <InventoryTab societyId={societyId} />}
    </PageLayout>
  );
};

const VENDOR_INIT = {
  name: '', category: '', phone: '', email: '', address: '', gstin: '',
  contactPerson: '', bankDetails: '', contractStartDate: '', contractEndDate: '', notes: '',
};

type VendorForm = typeof VENDOR_INIT;

const VendorsTab: React.FC<{ societyId: string }> = ({ societyId }) => {
  const [items, setItems] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(VENDOR_INIT);
  const [contractDocUrl, setContractDocUrl] = useState('');
  const [uploadingDoc, setUploadingDoc] = useState(false);

  const [editTarget, setEditTarget] = useState<Vendor | null>(null);
  const [editForm, setEditForm] = useState(VENDOR_INIT);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [editDocUrl, setEditDocUrl] = useState('');
  const [editUploadingDoc, setEditUploadingDoc] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<Vendor | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const refresh = () => {
    vendorApi.list(societyId).then(setItems).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(refresh, [societyId]);

  const handleDocChange = async (e: React.ChangeEvent<HTMLInputElement>, target: 'create' | 'edit') => {
    const file = e.target.files?.[0];
    if (!file) return;
    target === 'create' ? setUploadingDoc(true) : setEditUploadingDoc(true);
    try {
      const { url } = await uploadsApi.upload(societyId, file);
      target === 'create' ? setContractDocUrl(url) : setEditDocUrl(url);
    } catch { /* ignore */ } finally {
      target === 'create' ? setUploadingDoc(false) : setEditUploadingDoc(false);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const created = await vendorApi.create(societyId, {
        ...form,
        contractDocUrl: contractDocUrl || undefined,
      });
      setItems(p => [created, ...p]);
      setShowForm(false); setForm(VENDOR_INIT); setContractDocUrl('');
    } catch (err) {
      setError((err as ApiError).message || 'Failed to add vendor');
    } finally { setSaving(false); }
  };

  const openEdit = (v: Vendor) => {
    setEditTarget(v);
    setEditForm({
      name: v.name, category: v.category || '', phone: v.phone || '', email: v.email || '',
      address: v.address || '', gstin: v.gstin || '', contactPerson: v.contactPerson || '',
      bankDetails: v.bankDetails || '', contractStartDate: v.contractStartDate || '',
      contractEndDate: v.contractEndDate || '', notes: v.notes || '',
    });
    setEditDocUrl(v.contractDocUrl || '');
    setEditError('');
  };

  const submitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editTarget) return;
    setEditSaving(true); setEditError('');
    try {
      const updated = await vendorApi.update(societyId, editTarget.id, {
        ...editForm,
        contractDocUrl: editDocUrl || undefined,
      });
      setItems(p => p.map(v => v.id === editTarget.id ? updated : v));
      setEditTarget(null);
    } catch (err) {
      setEditError((err as ApiError).message || 'Failed to update vendor');
    } finally { setEditSaving(false); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true); setDeleteError('');
    try {
      await vendorApi.delete(societyId, deleteTarget.id);
      setItems(p => p.filter(v => v.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      setDeleteError((err as ApiError).message || 'Failed to delete vendor.');
    } finally { setDeleting(false); }
  };

  const fields = (
    f: VendorForm,
    setF: React.Dispatch<React.SetStateAction<VendorForm>>,
    docUrl: string,
    onDoc: (e: React.ChangeEvent<HTMLInputElement>) => void,
    uploading: boolean,
  ) => (
    <>
      <div>
        <label style={lbl}>Name *</label>
        <input value={f.name} onChange={e => setF(p => ({ ...p, name: e.target.value }))} required style={inp} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={lbl}>Category</label>
          <input value={f.category} onChange={e => setF(p => ({ ...p, category: e.target.value }))} placeholder="Plumbing" style={inp} />
        </div>
        <div>
          <label style={lbl}>Phone</label>
          <input value={f.phone} onChange={e => setF(p => ({ ...p, phone: e.target.value }))} style={inp} />
        </div>
      </div>
      <div>
        <label style={lbl}>Email</label>
        <input type="email" value={f.email} onChange={e => setF(p => ({ ...p, email: e.target.value }))} style={inp} />
      </div>
      <div>
        <label style={lbl}>Address</label>
        <input value={f.address} onChange={e => setF(p => ({ ...p, address: e.target.value }))} style={inp} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={lbl}>GSTIN</label>
          <input value={f.gstin} onChange={e => setF(p => ({ ...p, gstin: e.target.value }))} style={inp} />
        </div>
        <div>
          <label style={lbl}>Contact Person</label>
          <input value={f.contactPerson} onChange={e => setF(p => ({ ...p, contactPerson: e.target.value }))} style={inp} />
        </div>
      </div>
      <div>
        <label style={lbl}>Bank Details</label>
        <input value={f.bankDetails} onChange={e => setF(p => ({ ...p, bankDetails: e.target.value }))} placeholder="Account no., IFSC" style={inp} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={lbl}>Contract Start</label>
          <input type="date" value={f.contractStartDate} onChange={e => setF(p => ({ ...p, contractStartDate: e.target.value }))} style={inp} />
        </div>
        <div>
          <label style={lbl}>Contract End</label>
          <input type="date" value={f.contractEndDate} onChange={e => setF(p => ({ ...p, contractEndDate: e.target.value }))} style={inp} />
        </div>
      </div>
      <div>
        <label style={lbl}>Contract Document</label>
        <input type="file" accept="application/pdf,image/*" onChange={onDoc} style={inp} />
        {uploading && <p style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 4 }}>Uploading…</p>}
        {docUrl && !uploading && <p style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 4 }}>Uploaded.</p>}
      </div>
      <div>
        <label style={lbl}>Notes</label>
        <textarea value={f.notes} onChange={e => setF(p => ({ ...p, notes: e.target.value }))} style={{ ...inp, minHeight: 60, resize: 'vertical' }} />
      </div>
    </>
  );

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button onClick={() => setShowForm(true)} className="nx-btn nx-btn-primary" style={{ gap: 6 }}>
          <Plus size={14} /> Add Vendor
        </button>
      </div>
      <div className="nx-card" style={{ padding: 0, overflow: 'hidden' }}>
        <DataGrid
          columns={[
            { key: 'name', header: 'Name', cellStyle: { fontWeight: 500 }, render: (v: Vendor) => v.name },
            { key: 'category', header: 'Category', render: (v: Vendor) => v.category || '—' },
            { key: 'phone', header: 'Phone', render: (v: Vendor) => v.phone || '—' },
            { key: 'email', header: 'Email', render: (v: Vendor) => v.email || '—' },
            {
              key: 'status', header: 'Status',
              render: (v: Vendor) => (
                <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 500, background: v.active ? 'rgba(16,185,129,0.1)' : 'rgba(148,163,184,0.1)', color: v.active ? '#10B981' : '#64748B' }}>
                  {v.active ? 'Active' : 'Inactive'}
                </span>
              ),
            },
            {
              key: 'actions', header: '',
              render: (v: Vendor) => (
                <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                  <button style={actionBtn} onClick={() => openEdit(v)} title="Edit vendor"><Pencil size={14} /></button>
                  <button style={{ ...actionBtn, color: 'var(--nx-red)' }} onClick={() => { setDeleteTarget(v); setDeleteError(''); }} title="Delete vendor"><Trash2 size={14} /></button>
                </div>
              ),
            },
          ]}
          rows={items}
          rowKey={v => v.id}
          loading={loading}
          emptyIcon={<Truck size={32} style={{ color: 'var(--nx-text-muted)' }} />}
          emptyText="No vendors added yet."
        />
      </div>
      {showForm && (
        <Modal title="Add Vendor" submitLabel="Add Vendor" error={error} isLoading={saving} onClose={() => { setShowForm(false); setForm(VENDOR_INIT); setContractDocUrl(''); setError(''); }} onSubmit={submit}>
          {fields(form, setForm, contractDocUrl, e => handleDocChange(e, 'create'), uploadingDoc)}
        </Modal>
      )}
      {editTarget && (
        <Modal title={`Edit ${editTarget.name}`} submitLabel="Save Changes" error={editError} isLoading={editSaving} onClose={() => setEditTarget(null)} onSubmit={submitEdit}>
          {fields(editForm, setEditForm, editDocUrl, e => handleDocChange(e, 'edit'), editUploadingDoc)}
        </Modal>
      )}
      <ConfirmDialog
        open={!!deleteTarget}
        variant="danger"
        title={`Delete "${deleteTarget?.name}"?`}
        message="This vendor will be permanently removed. Vendors with existing AMC contracts or purchase orders cannot be deleted."
        confirmLabel="Delete Vendor"
        isLoading={deleting}
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={() => { setDeleteTarget(null); setDeleteError(''); }}
      />
    </>
  );
};

const AssetsTab: React.FC<{ societyId: string }> = ({ societyId }) => {
  const [items, setItems] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', category: '', location: '' });
  const [detailAsset, setDetailAsset] = useState<Asset | null>(null);

  const [editTarget, setEditTarget] = useState<Asset | null>(null);
  const [editForm, setEditForm] = useState({ name: '', category: '', location: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  const [deleteTarget, setDeleteTarget] = useState<Asset | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    assetApi.list(societyId).then(setItems).catch(() => {}).finally(() => setLoading(false));
  }, [societyId]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const created = await assetApi.create(societyId, form);
      setItems(p => [created, ...p]);
      setShowForm(false); setForm({ name: '', category: '', location: '' });
    } catch (err) {
      setError((err as ApiError).message || 'Failed to add asset');
    } finally { setSaving(false); }
  };

  const openEdit = (a: Asset) => {
    setEditTarget(a);
    setEditForm({ name: a.name, category: a.category || '', location: a.location || '' });
    setEditError('');
  };

  const submitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editTarget) return;
    setEditSaving(true); setEditError('');
    try {
      const updated = await assetApi.update(societyId, editTarget.id, editForm);
      setItems(p => p.map(a => a.id === editTarget.id ? updated : a));
      setEditTarget(null);
    } catch (err) {
      setEditError((err as ApiError).message || 'Failed to update asset');
    } finally { setEditSaving(false); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true); setDeleteError('');
    try {
      await assetApi.delete(societyId, deleteTarget.id);
      setItems(p => p.filter(a => a.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      setDeleteError((err as ApiError).message || 'Failed to delete asset.');
    } finally { setDeleting(false); }
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button onClick={() => setShowForm(true)} className="nx-btn nx-btn-primary" style={{ gap: 6 }}>
          <Plus size={14} /> Add Asset
        </button>
      </div>
      <div className="nx-card" style={{ padding: 0, overflow: 'hidden' }}>
        <DataGrid
          columns={[
            { key: 'name', header: 'Name', cellStyle: { fontWeight: 500 }, render: (a: Asset) => a.name },
            { key: 'category', header: 'Category', render: (a: Asset) => a.category || '—' },
            { key: 'location', header: 'Location', render: (a: Asset) => a.location || '—' },
            {
              key: 'actions', header: '',
              render: (a: Asset) => (
                <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', alignItems: 'center' }}>
                  <button className="nx-btn nx-btn-outline nx-btn-sm" onClick={() => setDetailAsset(a)}>
                    Manage
                  </button>
                  <button style={actionBtn} onClick={() => openEdit(a)} title="Edit asset"><Pencil size={14} /></button>
                  <button style={{ ...actionBtn, color: 'var(--nx-red)' }} onClick={() => { setDeleteTarget(a); setDeleteError(''); }} title="Delete asset"><Trash2 size={14} /></button>
                </div>
              ),
            },
          ]}
          rows={items}
          rowKey={a => a.id}
          loading={loading}
          emptyIcon={<Wrench size={32} style={{ color: 'var(--nx-text-muted)' }} />}
          emptyText="No assets added yet."
        />
      </div>
      {showForm && (
        <Modal title="Add Asset" submitLabel="Add Asset" error={error} isLoading={saving} onClose={() => setShowForm(false)} onSubmit={submit}>
          <div>
            <label style={lbl}>Name *</label>
            <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required placeholder="Generator #1" style={inp} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={lbl}>Category</label>
              <input value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} placeholder="Electrical" style={inp} />
            </div>
            <div>
              <label style={lbl}>Location</label>
              <input value={form.location} onChange={e => setForm(p => ({ ...p, location: e.target.value }))} placeholder="Basement" style={inp} />
            </div>
          </div>
        </Modal>
      )}
      {editTarget && (
        <Modal title={`Edit ${editTarget.name}`} submitLabel="Save Changes" error={editError} isLoading={editSaving} onClose={() => setEditTarget(null)} onSubmit={submitEdit}>
          <div>
            <label style={lbl}>Name *</label>
            <input value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} required placeholder="Generator #1" style={inp} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={lbl}>Category</label>
              <input value={editForm.category} onChange={e => setEditForm(p => ({ ...p, category: e.target.value }))} placeholder="Electrical" style={inp} />
            </div>
            <div>
              <label style={lbl}>Location</label>
              <input value={editForm.location} onChange={e => setEditForm(p => ({ ...p, location: e.target.value }))} placeholder="Basement" style={inp} />
            </div>
          </div>
        </Modal>
      )}
      {detailAsset && (
        <AssetDetailModal societyId={societyId} asset={detailAsset} onClose={() => setDetailAsset(null)} />
      )}
      <ConfirmDialog
        open={!!deleteTarget}
        variant="danger"
        title={`Delete "${deleteTarget?.name}"?`}
        message="This asset will be permanently removed, along with its preventive maintenance schedule and maintenance history."
        confirmLabel="Delete Asset"
        isLoading={deleting}
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={() => { setDeleteTarget(null); setDeleteError(''); }}
      />
    </>
  );
};

const AssetDetailModal: React.FC<{ societyId: string; asset: Asset; onClose: () => void }> = ({ societyId, asset, onClose }) => {
  const [schedules, setSchedules] = useState<PmSchedule[]>([]);
  const [logs, setLogs] = useState<AssetMaintenanceLog[]>([]);
  const [scheduleForm, setScheduleForm] = useState({ frequencyDays: '30', nextDueDate: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [logQuery, setLogQuery] = useState('');

  const refresh = () => {
    assetApi.listPmSchedules(societyId, asset.id).then(setSchedules).catch(() => {});
    assetApi.listMaintenanceLogs(societyId, asset.id).then(setLogs).catch(() => {});
  };

  useEffect(refresh, [societyId, asset.id]);

  const addSchedule = async () => {
    if (!scheduleForm.nextDueDate) return;
    setSaving(true); setError('');
    try {
      await assetApi.addPmSchedule(societyId, {
        assetId: asset.id,
        frequencyDays: Number(scheduleForm.frequencyDays),
        nextDueDate: scheduleForm.nextDueDate,
      });
      setScheduleForm({ frequencyDays: '30', nextDueDate: '' });
      refresh();
    } catch (err) {
      setError((err as ApiError).message || 'Failed to add PM schedule');
    } finally { setSaving(false); }
  };

  const complete = async (scheduleId: string) => {
    try {
      await assetApi.completePmSchedule(societyId, asset.id, scheduleId, {});
      refresh();
    } catch { /* ignore */ }
  };

  const filteredLogs = logQuery.trim()
    ? logs.filter(l => `${l.performedDate} ${l.notes ?? ''}`.toLowerCase().includes(logQuery.trim().toLowerCase()))
    : logs;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(8,14,29,0.75)', backdropFilter: 'blur(4px)' }} />
      <div style={{
        position: 'relative', zIndex: 1, width: '100%', maxWidth: 560,
        background: 'var(--nx-surface-1)', borderRadius: 16, border: '1px solid var(--nx-border)',
        boxShadow: '0 24px 80px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column', maxHeight: '86vh',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--nx-border)' }}>
          <h2 style={{ fontFamily: 'Syne, sans-serif', fontSize: 15, fontWeight: 700, color: 'var(--nx-text-primary)' }}>{asset.name}</h2>
          <button type="button" onClick={onClose} style={{ width: 28, height: 28, border: 'none', background: 'var(--nx-surface-3)', borderRadius: 7, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--nx-text-muted)' }}>
            <X size={14} />
          </button>
        </div>
        <div style={{ padding: '20px 24px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {error && (
            <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, fontSize: 12, color: '#EF4444' }}>{error}</div>
          )}
          <div>
            <label style={lbl}>Preventive Maintenance Schedule</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, marginBottom: 10 }}>
              <input type="number" placeholder="Frequency (days)" value={scheduleForm.frequencyDays} onChange={e => setScheduleForm(p => ({ ...p, frequencyDays: e.target.value }))} style={inp} />
              <input type="date" value={scheduleForm.nextDueDate} onChange={e => setScheduleForm(p => ({ ...p, nextDueDate: e.target.value }))} style={inp} />
              <button type="button" className="nx-btn nx-btn-outline nx-btn-sm" disabled={saving} onClick={addSchedule}>Add</button>
            </div>
            {schedules.length === 0 ? (
              <p style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>No PM schedule yet.</p>
            ) : schedules.map(s => (
              <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: 'var(--nx-surface-3)', borderRadius: 8, fontSize: 12, marginBottom: 6 }}>
                <span>Every {s.frequencyDays}d · next due {s.nextDueDate}{s.lastCompletedDate ? ` · last done ${s.lastCompletedDate}` : ''}</span>
                <button type="button" className="nx-btn nx-btn-outline nx-btn-sm" onClick={() => complete(s.id)} style={{ gap: 4 }}>
                  <CheckCircle2 size={12} /> Mark Complete
                </button>
              </div>
            ))}
          </div>
          <div>
            <label style={lbl}>Maintenance History</label>
            {logs.length === 0 ? (
              <p style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>No maintenance recorded yet.</p>
            ) : (
              <>
                {logs.length > 5 && (
                  <input
                    value={logQuery}
                    onChange={e => setLogQuery(e.target.value)}
                    placeholder="Search by date or notes…"
                    style={{ ...inp, marginBottom: 8 }}
                  />
                )}
                <div style={{ maxHeight: 240, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {filteredLogs.length === 0 ? (
                    <p style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>No entries match "{logQuery}".</p>
                  ) : filteredLogs.map(l => (
                    <div key={l.id} style={{ padding: '8px 10px', background: 'var(--nx-surface-3)', borderRadius: 8, fontSize: 12 }}>
                      <span>{l.performedDate}{l.cost ? ` · ${fmtAmount(l.cost)}` : ''}{l.notes ? ` · ${l.notes}` : ''}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const AMC_INIT = { vendorId: '', description: '', startDate: '', endDate: '', amount: '' };
type AmcForm = typeof AMC_INIT;

const AmcTab: React.FC<{ societyId: string }> = ({ societyId }) => {
  const [items, setItems] = useState<AmcContract[]>([]);
  const [vendorList, setVendorList] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(AMC_INIT);

  const [editTarget, setEditTarget] = useState<AmcContract | null>(null);
  const [editForm, setEditForm] = useState(AMC_INIT);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  const [deleteTarget, setDeleteTarget] = useState<AmcContract | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    amcApi.list(societyId).then(setItems).catch(() => {}).finally(() => setLoading(false));
    vendorApi.list(societyId).then(setVendorList).catch(() => {});
  }, [societyId]);

  const toReq = (f: AmcForm) => ({
    vendorId: f.vendorId,
    description: f.description || undefined,
    startDate: f.startDate,
    endDate: f.endDate,
    amount: f.amount ? Number(f.amount) : undefined,
  });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const created = await amcApi.create(societyId, toReq(form));
      setItems(p => [created, ...p]);
      setShowForm(false); setForm(AMC_INIT);
    } catch (err) {
      setError((err as ApiError).message || 'Failed to add AMC contract');
    } finally { setSaving(false); }
  };

  const openEdit = (c: AmcContract) => {
    setEditTarget(c);
    setEditForm({
      vendorId: c.vendorId, description: c.description || '',
      startDate: c.startDate, endDate: c.endDate, amount: c.amount != null ? String(c.amount) : '',
    });
    setEditError('');
  };

  const submitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editTarget) return;
    setEditSaving(true); setEditError('');
    try {
      const updated = await amcApi.update(societyId, editTarget.id, toReq(editForm));
      setItems(p => p.map(c => c.id === editTarget.id ? updated : c));
      setEditTarget(null);
    } catch (err) {
      setEditError((err as ApiError).message || 'Failed to update AMC contract');
    } finally { setEditSaving(false); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true); setDeleteError('');
    try {
      await amcApi.delete(societyId, deleteTarget.id);
      setItems(p => p.filter(c => c.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      setDeleteError((err as ApiError).message || 'Failed to delete AMC contract.');
    } finally { setDeleting(false); }
  };

  const vendorName = (id: string) => vendorList.find(v => v.id === id)?.name ?? id.slice(0, 8);
  const isDueSoon = (endDate: string) => {
    const days = (new Date(endDate).getTime() - Date.now()) / 86400000;
    return days >= 0 && days <= 30;
  };
  const isExpired = (endDate: string) => new Date(endDate).getTime() < Date.now();

  const fields = (f: AmcForm, setF: React.Dispatch<React.SetStateAction<AmcForm>>) => (
    <>
      <div>
        <label style={lbl}>Vendor *</label>
        <SearchSelect
          value={f.vendorId}
          onChange={v => setF(p => ({ ...p, vendorId: v }))}
          options={vendorList.map(v => ({ value: v.id, label: v.name }))}
          placeholder="Search vendors…"
          required
        />
      </div>
      <div>
        <label style={lbl}>Description</label>
        <input value={f.description} onChange={e => setF(p => ({ ...p, description: e.target.value }))} placeholder="Generator AMC" style={inp} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={lbl}>Start Date *</label>
          <input type="date" value={f.startDate} onChange={e => setF(p => ({ ...p, startDate: e.target.value }))} required style={inp} />
        </div>
        <div>
          <label style={lbl}>End Date *</label>
          <input type="date" value={f.endDate} onChange={e => setF(p => ({ ...p, endDate: e.target.value }))} required style={inp} />
        </div>
      </div>
      <div>
        <label style={lbl}>Amount (₹)</label>
        <input type="number" value={f.amount} onChange={e => setF(p => ({ ...p, amount: e.target.value }))} style={inp} />
      </div>
    </>
  );

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button onClick={() => setShowForm(true)} className="nx-btn nx-btn-primary" style={{ gap: 6 }}>
          <Plus size={14} /> Add AMC Contract
        </button>
      </div>
      <div className="nx-card" style={{ padding: 0, overflow: 'hidden' }}>
        <DataGrid
          columns={[
            { key: 'vendor', header: 'Vendor', cellStyle: { fontWeight: 500 }, render: (c: AmcContract) => vendorName(c.vendorId) },
            { key: 'description', header: 'Description', render: (c: AmcContract) => c.description || '—' },
            { key: 'period', header: 'Period', render: (c: AmcContract) => `${c.startDate} → ${c.endDate}` },
            { key: 'amount', header: 'Amount', render: (c: AmcContract) => fmtAmount(c.amount) },
            {
              key: 'status', header: 'Status',
              render: (c: AmcContract) => {
                const expired = isExpired(c.endDate);
                const dueSoon = !expired && isDueSoon(c.endDate);
                const bg = expired ? 'rgba(148,163,184,0.1)' : dueSoon ? 'rgba(245,158,11,0.1)' : 'rgba(16,185,129,0.1)';
                const text = expired ? '#64748B' : dueSoon ? '#D97706' : '#10B981';
                return (
                  <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 500, background: bg, color: text }}>
                    {expired ? 'Expired' : dueSoon ? 'Renewal Due Soon' : 'Active'}
                  </span>
                );
              },
            },
            {
              key: 'actions', header: '',
              render: (c: AmcContract) => (
                <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                  <button style={actionBtn} onClick={() => openEdit(c)} title="Edit AMC contract"><Pencil size={14} /></button>
                  <button style={{ ...actionBtn, color: 'var(--nx-red)' }} onClick={() => { setDeleteTarget(c); setDeleteError(''); }} title="Delete AMC contract"><Trash2 size={14} /></button>
                </div>
              ),
            },
          ]}
          rows={items}
          rowKey={c => c.id}
          loading={loading}
          emptyIcon={<FileClock size={32} style={{ color: 'var(--nx-text-muted)' }} />}
          emptyText="No AMC contracts yet."
        />
      </div>
      {showForm && (
        <Modal title="Add AMC Contract" submitLabel="Add Contract" error={error} isLoading={saving} onClose={() => { setShowForm(false); setForm(AMC_INIT); setError(''); }} onSubmit={submit}>
          {fields(form, setForm)}
        </Modal>
      )}
      {editTarget && (
        <Modal title="Edit AMC Contract" submitLabel="Save Changes" error={editError} isLoading={editSaving} onClose={() => setEditTarget(null)} onSubmit={submitEdit}>
          {fields(editForm, setEditForm)}
        </Modal>
      )}
      <ConfirmDialog
        open={!!deleteTarget}
        variant="danger"
        title="Delete AMC contract?"
        message={`Remove the ${deleteTarget ? vendorName(deleteTarget.vendorId) : ''} contract? This action cannot be undone.`}
        confirmLabel="Delete Contract"
        isLoading={deleting}
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={() => { setDeleteTarget(null); setDeleteError(''); }}
      />
    </>
  );
};

const poStatusColor = (s: string) =>
  s === 'PAID' ? { bg: 'rgba(16,185,129,0.1)', text: '#10B981' }
    : s === 'GRN_RECEIVED' ? { bg: 'rgba(26,108,246,0.1)', text: '#1A6CF6' }
    : s === 'RAISED' ? { bg: 'rgba(245,158,11,0.1)', text: '#D97706' }
    : { bg: 'rgba(148,163,184,0.1)', text: '#64748B' };

const PO_INIT = { vendorId: '', description: '', amount: '' };
type PoForm = typeof PO_INIT;

const PurchaseOrdersTab: React.FC<{ societyId: string }> = ({ societyId }) => {
  const [items, setItems] = useState<PurchaseOrder[]>([]);
  const [vendorList, setVendorList] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(PO_INIT);
  const [acting, setActing] = useState<string | null>(null);

  const [editTarget, setEditTarget] = useState<PurchaseOrder | null>(null);
  const [editForm, setEditForm] = useState(PO_INIT);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  const [deleteTarget, setDeleteTarget] = useState<PurchaseOrder | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const refresh = () => {
    poApi.list(societyId).then(setItems).catch(() => {}).finally(() => setLoading(false));
    vendorApi.list(societyId).then(setVendorList).catch(() => {});
  };
  useEffect(refresh, [societyId]);

  const vendorName = (id: string) => vendorList.find(v => v.id === id)?.name ?? id.slice(0, 8);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.vendorId || !form.amount) return;
    setSaving(true); setError('');
    try {
      await poApi.create(societyId, { vendorId: form.vendorId, description: form.description || undefined, amount: Number(form.amount) });
      setShowForm(false); setForm(PO_INIT);
      refresh();
    } catch (err) {
      setError((err as ApiError).message || 'Failed to create purchase order');
    } finally { setSaving(false); }
  };

  const openEdit = (p: PurchaseOrder) => {
    setEditTarget(p);
    setEditForm({ vendorId: p.vendorId, description: p.description || '', amount: String(p.amount) });
    setEditError('');
  };

  const submitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editTarget || !editForm.vendorId || !editForm.amount) return;
    setEditSaving(true); setEditError('');
    try {
      await poApi.update(societyId, editTarget.id, { vendorId: editForm.vendorId, description: editForm.description || undefined, amount: Number(editForm.amount) });
      setEditTarget(null);
      refresh();
    } catch (err) {
      setEditError((err as ApiError).message || 'Failed to update purchase order');
    } finally { setEditSaving(false); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true); setDeleteError('');
    try {
      await poApi.delete(societyId, deleteTarget.id);
      setItems(p => p.filter(x => x.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      setDeleteError((err as ApiError).message || 'Failed to delete purchase order.');
    } finally { setDeleting(false); }
  };

  const act = async (id: string, action: 'raise' | 'grn' | 'pay') => {
    setActing(id);
    try {
      if (action === 'raise') await poApi.raise(societyId, id);
      else if (action === 'grn') await poApi.recordGrn(societyId, id);
      else await poApi.markPaid(societyId, id);
      refresh();
    } catch { /* ignore */ } finally { setActing(null); }
  };

  const fields = (f: PoForm, setF: React.Dispatch<React.SetStateAction<PoForm>>) => (
    <>
      <div>
        <label style={lbl}>Vendor *</label>
        <SearchSelect
          value={f.vendorId}
          onChange={v => setF(p => ({ ...p, vendorId: v }))}
          options={vendorList.map(v => ({ value: v.id, label: v.name }))}
          placeholder="Search vendors…"
          required
        />
      </div>
      <div>
        <label style={lbl}>Description</label>
        <input value={f.description} onChange={e => setF(p => ({ ...p, description: e.target.value }))} style={inp} />
      </div>
      <div>
        <label style={lbl}>Amount (₹) *</label>
        <input type="number" value={f.amount} onChange={e => setF(p => ({ ...p, amount: e.target.value }))} required style={inp} />
      </div>
    </>
  );

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button onClick={() => setShowForm(true)} className="nx-btn nx-btn-primary" style={{ gap: 6 }}>
          <Plus size={14} /> Raise Purchase Order
        </button>
      </div>
      <div className="nx-card" style={{ padding: 0, overflow: 'hidden' }}>
        <DataGrid
          columns={[
            { key: 'vendor', header: 'Vendor', cellStyle: { fontWeight: 500 }, render: (p: PurchaseOrder) => vendorName(p.vendorId) },
            { key: 'description', header: 'Description', render: (p: PurchaseOrder) => p.description || '—' },
            { key: 'amount', header: 'Amount', render: (p: PurchaseOrder) => fmtAmount(p.amount) },
            {
              key: 'status', header: 'Status',
              render: (p: PurchaseOrder) => {
                const sc = poStatusColor(p.status);
                return <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 500, background: sc.bg, color: sc.text }}>{p.status.replace('_', ' ')}</span>;
              },
            },
            {
              key: 'actions', header: '',
              render: (p: PurchaseOrder) => (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'flex-end' }}>
                  {p.status === 'DRAFT' && (
                    <button className="nx-btn nx-btn-outline nx-btn-sm" disabled={acting === p.id} onClick={() => act(p.id, 'raise')}>Raise</button>
                  )}
                  {p.status === 'RAISED' && (
                    <button className="nx-btn nx-btn-outline nx-btn-sm" disabled={acting === p.id} onClick={() => act(p.id, 'grn')}>Record GRN</button>
                  )}
                  {p.status === 'GRN_RECEIVED' && (
                    <button className="nx-btn nx-btn-outline nx-btn-sm" disabled={acting === p.id} onClick={() => act(p.id, 'pay')}>Mark Paid</button>
                  )}
                  {p.status === 'DRAFT' && (
                    <>
                      <button style={actionBtn} onClick={() => openEdit(p)} title="Edit purchase order"><Pencil size={14} /></button>
                      <button style={{ ...actionBtn, color: 'var(--nx-red)' }} onClick={() => { setDeleteTarget(p); setDeleteError(''); }} title="Delete purchase order"><Trash2 size={14} /></button>
                    </>
                  )}
                </div>
              ),
            },
          ]}
          rows={items}
          rowKey={p => p.id}
          loading={loading}
          emptyIcon={<ClipboardList size={32} style={{ color: 'var(--nx-text-muted)' }} />}
          emptyText="No purchase orders yet."
        />
      </div>
      {showForm && (
        <Modal title="Raise Purchase Order" submitLabel="Create" error={error} isLoading={saving} onClose={() => { setShowForm(false); setForm(PO_INIT); setError(''); }} onSubmit={submit}>
          {fields(form, setForm)}
        </Modal>
      )}
      {editTarget && (
        <Modal title="Edit Purchase Order" submitLabel="Save Changes" error={editError} isLoading={editSaving} onClose={() => setEditTarget(null)} onSubmit={submitEdit}>
          {fields(editForm, setEditForm)}
        </Modal>
      )}
      <ConfirmDialog
        open={!!deleteTarget}
        variant="danger"
        title="Delete purchase order?"
        message={`Remove this draft PO to ${deleteTarget ? vendorName(deleteTarget.vendorId) : ''}? This action cannot be undone.`}
        confirmLabel="Delete PO"
        isLoading={deleting}
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={() => { setDeleteTarget(null); setDeleteError(''); }}
      />
    </>
  );
};

const INVENTORY_INIT = { name: '', category: 'CONSUMABLE' as InventoryItem['category'], unit: '', reorderLevel: '0' };
type InventoryForm = typeof INVENTORY_INIT;

const InventoryTab: React.FC<{ societyId: string }> = ({ societyId }) => {
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(INVENTORY_INIT);
  const [detailItem, setDetailItem] = useState<InventoryItem | null>(null);

  const [editTarget, setEditTarget] = useState<InventoryItem | null>(null);
  const [editForm, setEditForm] = useState(INVENTORY_INIT);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  const [deleteTarget, setDeleteTarget] = useState<InventoryItem | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const refresh = () => {
    inventoryApi.listItems(societyId).then(setItems).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(refresh, [societyId]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      await inventoryApi.createItem(societyId, { ...form, reorderLevel: Number(form.reorderLevel) });
      setShowForm(false); setForm(INVENTORY_INIT);
      refresh();
    } catch (err) {
      setError((err as ApiError).message || 'Failed to add inventory item');
    } finally { setSaving(false); }
  };

  const openEdit = (i: InventoryItem) => {
    setEditTarget(i);
    setEditForm({ name: i.name, category: i.category, unit: i.unit || '', reorderLevel: String(i.reorderLevel) });
    setEditError('');
  };

  const submitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editTarget) return;
    setEditSaving(true); setEditError('');
    try {
      await inventoryApi.updateItem(societyId, editTarget.id, { ...editForm, reorderLevel: Number(editForm.reorderLevel) });
      setEditTarget(null);
      refresh();
    } catch (err) {
      setEditError((err as ApiError).message || 'Failed to update inventory item');
    } finally { setEditSaving(false); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true); setDeleteError('');
    try {
      await inventoryApi.deleteItem(societyId, deleteTarget.id);
      setItems(p => p.filter(i => i.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      setDeleteError((err as ApiError).message || 'Failed to delete inventory item.');
    } finally { setDeleting(false); }
  };

  const fields = (f: InventoryForm, setF: React.Dispatch<React.SetStateAction<InventoryForm>>) => (
    <>
      <div>
        <label style={lbl}>Name *</label>
        <input value={f.name} onChange={e => setF(p => ({ ...p, name: e.target.value }))} required placeholder="Cleaning Supplies" style={inp} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={lbl}>Category</label>
          <select value={f.category} onChange={e => setF(p => ({ ...p, category: e.target.value as InventoryItem['category'] }))} style={inp}>
            <option value="CONSUMABLE">Consumable</option>
            <option value="ASSET">Asset</option>
          </select>
        </div>
        <div>
          <label style={lbl}>Unit</label>
          <input value={f.unit} onChange={e => setF(p => ({ ...p, unit: e.target.value }))} placeholder="pcs, liters" style={inp} />
        </div>
      </div>
      <div>
        <label style={lbl}>Reorder Level</label>
        <input type="number" value={f.reorderLevel} onChange={e => setF(p => ({ ...p, reorderLevel: e.target.value }))} style={inp} />
      </div>
    </>
  );

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button onClick={() => setShowForm(true)} className="nx-btn nx-btn-primary" style={{ gap: 6 }}>
          <Plus size={14} /> Add Item
        </button>
      </div>
      <div className="nx-card" style={{ padding: 0, overflow: 'hidden' }}>
        <DataGrid
          columns={[
            { key: 'name', header: 'Name', cellStyle: { fontWeight: 500 }, render: (i: InventoryItem) => i.name },
            { key: 'category', header: 'Category', render: (i: InventoryItem) => i.category },
            { key: 'quantity', header: 'Quantity', render: (i: InventoryItem) => `${i.quantity}${i.unit ? ` ${i.unit}` : ''}` },
            {
              key: 'stock', header: 'Stock',
              render: (i: InventoryItem) => i.quantity <= i.reorderLevel
                ? <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 500, background: 'rgba(239,68,68,0.1)', color: '#DC2626' }}>Reorder</span>
                : <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 500, background: 'rgba(16,185,129,0.1)', color: '#10B981' }}>OK</span>,
            },
            {
              key: 'actions', header: '',
              render: (i: InventoryItem) => (
                <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', alignItems: 'center' }}>
                  <button className="nx-btn nx-btn-outline nx-btn-sm" onClick={() => setDetailItem(i)}>Manage</button>
                  <button style={actionBtn} onClick={() => openEdit(i)} title="Edit item"><Pencil size={14} /></button>
                  <button style={{ ...actionBtn, color: 'var(--nx-red)' }} onClick={() => { setDeleteTarget(i); setDeleteError(''); }} title="Delete item"><Trash2 size={14} /></button>
                </div>
              ),
            },
          ]}
          rows={items}
          rowKey={i => i.id}
          loading={loading}
          emptyIcon={<Boxes size={32} style={{ color: 'var(--nx-text-muted)' }} />}
          emptyText="No inventory items yet."
        />
      </div>
      {showForm && (
        <Modal title="Add Inventory Item" submitLabel="Add Item" error={error} isLoading={saving} onClose={() => { setShowForm(false); setForm(INVENTORY_INIT); setError(''); }} onSubmit={submit}>
          {fields(form, setForm)}
        </Modal>
      )}
      {editTarget && (
        <Modal title={`Edit ${editTarget.name}`} submitLabel="Save Changes" error={editError} isLoading={editSaving} onClose={() => setEditTarget(null)} onSubmit={submitEdit}>
          {fields(editForm, setEditForm)}
        </Modal>
      )}
      {detailItem && (
        <InventoryDetailModal societyId={societyId} item={detailItem} onClose={() => { setDetailItem(null); refresh(); }} />
      )}
      <ConfirmDialog
        open={!!deleteTarget}
        variant="danger"
        title={`Delete "${deleteTarget?.name}"?`}
        message="This item will be permanently removed, along with its stock transaction history."
        confirmLabel="Delete Item"
        isLoading={deleting}
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={() => { setDeleteTarget(null); setDeleteError(''); }}
      />
    </>
  );
};

const InventoryDetailModal: React.FC<{ societyId: string; item: InventoryItem; onClose: () => void }> = ({ societyId, item, onClose }) => {
  const [txns, setTxns] = useState<InventoryStockTransaction[]>([]);
  const [form, setForm] = useState({ type: 'IN' as 'IN' | 'OUT', quantity: '1', transactionDate: todayIsoLocal(), note: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const refresh = () => {
    inventoryApi.listTransactions(societyId, item.id).then(setTxns).catch(() => {});
  };
  useEffect(refresh, [societyId, item.id]);

  const submit = async () => {
    setSaving(true); setError('');
    try {
      await inventoryApi.addTransaction(societyId, item.id, {
        type: form.type, quantity: Number(form.quantity), transactionDate: form.transactionDate, note: form.note || undefined,
      });
      setForm(p => ({ ...p, quantity: '1', note: '' }));
      refresh();
    } catch (err) {
      setError((err as ApiError).message || 'Failed to record transaction');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(8,14,29,0.75)', backdropFilter: 'blur(4px)' }} />
      <div style={{
        position: 'relative', zIndex: 1, width: '100%', maxWidth: 520,
        background: 'var(--nx-surface-1)', borderRadius: 16, border: '1px solid var(--nx-border)',
        boxShadow: '0 24px 80px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column', maxHeight: '86vh',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--nx-border)' }}>
          <h2 style={{ fontFamily: 'Syne, sans-serif', fontSize: 15, fontWeight: 700, color: 'var(--nx-text-primary)' }}>
            {item.name} — {item.quantity}{item.unit ? ` ${item.unit}` : ''}
          </h2>
          <button type="button" onClick={onClose} style={{ width: 28, height: 28, border: 'none', background: 'var(--nx-surface-3)', borderRadius: 7, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--nx-text-muted)' }}>
            <X size={14} />
          </button>
        </div>
        <div style={{ padding: '20px 24px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {error && (
            <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, fontSize: 12, color: '#EF4444' }}>{error}</div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 8 }}>
            <select value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value as 'IN' | 'OUT' }))} style={inp}>
              <option value="IN">Stock In</option>
              <option value="OUT">Stock Out</option>
            </select>
            <input type="number" min="0" value={form.quantity} onChange={e => setForm(p => ({ ...p, quantity: e.target.value }))} style={inp} />
            <input type="date" value={form.transactionDate} onChange={e => setForm(p => ({ ...p, transactionDate: e.target.value }))} style={inp} />
            <button type="button" className="nx-btn nx-btn-outline nx-btn-sm" disabled={saving} onClick={submit}>Record</button>
          </div>
          <div>
            <label style={lbl}>Transaction Log</label>
            {txns.length === 0 ? (
              <p style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>No transactions yet.</p>
            ) : txns.map(t => (
              <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 10px', background: 'var(--nx-surface-3)', borderRadius: 8, fontSize: 12, marginBottom: 6 }}>
                <span>{t.transactionDate} · {t.type} · {t.quantity}{t.note ? ` · ${t.note}` : ''}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

function todayIsoLocal() {
  return new Date().toISOString().slice(0, 10);
}

export default AssetMaintenancePage;
